import _ from "lodash";
import {
  type PatchedAdminAthleticInfo,
  PrimaryPositionBaseballEnum,
  SecondaryPositionBaseballEnum,
  PrimaryPositionVolleyballEnum,
  SecondaryPositionVolleyballEnum,
  HandednessEnum,
  SkillLevelEnum,
  AlphaSizesEnum,
  JerseySizeEnum,
} from "@/api/model";
import { emptyOrBlank } from "./emptyOrBlank";
import * as formatters from "@/modules/profile/utils/formatters";
import type { AthleticInfoFormRows } from "../components/sections";

export const JERSEY_SIZE_LABELS: Record<JerseySizeEnum, string> = {
  [JerseySizeEnum.YXS]: "Youth XS",
  [JerseySizeEnum.YS]: "Youth S",
  [JerseySizeEnum.YM]: "Youth M",
  [JerseySizeEnum.YL]: "Youth L",
  [JerseySizeEnum.YXL]: "Youth XL",
  [JerseySizeEnum.S]: "Adult S",
  [JerseySizeEnum.M]: "Adult M",
  [JerseySizeEnum.L]: "Adult L",
  [JerseySizeEnum.XL]: "Adult XL",
  [JerseySizeEnum.XXL]: "Adult XXL",
};

export class CollapsableAthleticInfoPatchDTO
  implements PatchedAdminAthleticInfo
{
  primary_position_baseball?:
    | PatchedAdminAthleticInfo["primary_position_baseball"]
    | undefined;
  secondary_position_baseball?:
    | PatchedAdminAthleticInfo["secondary_position_baseball"]
    | undefined;
  primary_position_volleyball?:
    | PatchedAdminAthleticInfo["primary_position_volleyball"]
    | undefined;
  secondary_position_volleyball?:
    | PatchedAdminAthleticInfo["secondary_position_volleyball"]
    | undefined;
  handedness?: PatchedAdminAthleticInfo["handedness"] | undefined;
  throws?: PatchedAdminAthleticInfo["throws"] | undefined;
  bats?: PatchedAdminAthleticInfo["bats"] | undefined;
  skill_level?: PatchedAdminAthleticInfo["skill_level"] | undefined;
  jersey_top_size?: PatchedAdminAthleticInfo["jersey_top_size"] | undefined;
  jersey_bottom_size?:
    | PatchedAdminAthleticInfo["jersey_bottom_size"]
    | undefined;
  first_choice_jersey_number?:
    | PatchedAdminAthleticInfo["first_choice_jersey_number"]
    | undefined;
  second_choice_jersey_number?:
    | PatchedAdminAthleticInfo["second_choice_jersey_number"]
    | undefined;
  hat_size?: PatchedAdminAthleticInfo["hat_size"] | undefined;
  hat_size_numbered?: PatchedAdminAthleticInfo["hat_size_numbered"] | undefined;

  constructor(data: PatchedAdminAthleticInfo) {
    this.primary_position_baseball = data.primary_position_baseball;
    this.secondary_position_baseball = data.secondary_position_baseball;
    this.primary_position_volleyball = data.primary_position_volleyball;
    this.secondary_position_volleyball = data.secondary_position_volleyball;
    this.handedness = data.handedness;
    this.throws = data.throws;
    this.bats = data.bats;
    this.skill_level = data.skill_level;
    this.jersey_top_size = data.jersey_top_size;
    this.jersey_bottom_size = data.jersey_bottom_size;
    this.first_choice_jersey_number = data.first_choice_jersey_number;
    this.second_choice_jersey_number = data.second_choice_jersey_number;
    this.hat_size = data.hat_size;
    this.hat_size_numbered = data.hat_size_numbered;
  }

  toJSON(): PatchedAdminAthleticInfo {
    return _.omitBy(this, emptyOrBlank);
  }
}

export const athleticInfoRequestModelToViewModel = (
  requestModel: PatchedAdminAthleticInfo | null | undefined
): AthleticInfoFormRows => ({
  primaryPositionBaseball: {
    label: "Primary Position (Baseball)",
    data: requestModel?.primary_position_baseball,
    values: Object.values(PrimaryPositionBaseballEnum),
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.primary_position_baseball_updated_at
    ),
  },
  secondaryPositionBaseball: {
    label: "Secondary Position (Baseball)",
    data: requestModel?.secondary_position_baseball,
    values: Object.values(SecondaryPositionBaseballEnum),
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.secondary_position_baseball_updated_at
    ),
  },
  primaryPositionVolleyball: {
    label: "Primary Position (Volleyball)",
    data: requestModel?.primary_position_volleyball,
    values: Object.values(PrimaryPositionVolleyballEnum),
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.primary_position_volleyball_updated_at
    ),
  },
  secondaryPositionVolleyball: {
    label: "Secondary Position (Volleyball)",
    data: requestModel?.secondary_position_volleyball,
    values: Object.values(SecondaryPositionVolleyballEnum),
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.secondary_position_volleyball_updated_at
    ),
  },
  handedness: {
    label: "Handedness",
    data: requestModel?.handedness,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.handedness_updated_at
    ),
    values: Object.values(HandednessEnum),
  },
  throws: {
    label: "Throws",
    data: requestModel?.throws,
    lastUpdated: formatters.formatDisplayDate(requestModel?.throws_updated_at),
    values: Object.values(HandednessEnum),
  },
  bats: {
    label: "Bats",
    data: requestModel?.bats,
    lastUpdated: formatters.formatDisplayDate(requestModel?.bats_updated_at),
    values: Object.values(HandednessEnum),
  },
  skillLevel: {
    label: "Skill Level",
    data: requestModel?.skill_level,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.skill_level_updated_at
    ),
    values: Object.values(SkillLevelEnum),
  },
  jerseyTopSize: {
    label: "Jersey Top Size",
    data: requestModel?.jersey_top_size,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.jersey_top_size_updated_at
    ),
    values: Object.values(JerseySizeEnum).map((value) => ({
      label: JERSEY_SIZE_LABELS[value],
      value,
    })),
  },
  jerseyBottomSize: {
    label: "Jersey Bottom Size",
    data: requestModel?.jersey_bottom_size,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.jersey_bottom_size_updated_at
    ),
    values: Object.values(JerseySizeEnum).map((value) => ({
      label: JERSEY_SIZE_LABELS[value],
      value,
    })),
  },
  firstChoiceJerseyNumber: {
    label: "1st Choice Jersey Number",
    data: requestModel?.first_choice_jersey_number,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.first_choice_jersey_number_updated_at
    ),
  },
  secondChoiceJerseyNumber: {
    label: "2nd Choice Jersey Number",
    data: requestModel?.second_choice_jersey_number,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.second_choice_jersey_number_updated_at
    ),
  },
  hatSize: {
    label: "Hat Size",
    data: requestModel?.hat_size,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.hat_size_updated_at
    ),
    values: Object.values(AlphaSizesEnum),
  },
  hatSizeNumbered: {
    label: "Hat Size (Numbered)",
    data: requestModel?.hat_size_numbered,
    lastUpdated: formatters.formatDisplayDate(
      requestModel?.hat_size_numbered_updated_at
    ),
  },
});
